import { useCallback, useEffect, useState } from "react";

import { Visibility as VisibilityIcon } from "@mui/icons-material";
import { Button, Grid, IconButton, Stack } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Link, useNavigate } from "react-router-dom";

import { useAuth, useOutletApi } from "@/features/authentication";
import { UserOutletsSelect } from "@/features/outlets";
import { PermissionEnum } from "@/features/permissions";
import { formatDate, formatNumber, getErrorMessage } from "@/features/shared";
import {
  CustomDateRangePicker,
  PageContainer,
  SearchTextField,
  useSnackbar,
} from "@/features/ui";
import {
  useDateRangeFilter,
  useSearchFilter,
  useServerPagination,
} from "@/hooks";

export const OtherPayments = () => {
  const { outlet, changeOutlet, hasPermission } = useAuth();
  const outletApi = useOutletApi();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const [otherPayments, setOtherPayments] = useState([]);
  const [loading, setLoading] = useState(false);

  const { setRowCount, paginationParams, dataGridPaginationProps, resetParams } =
    useServerPagination();
  const { keyword, searchProps } = useSearchFilter({ resetParams });
  const { dateRangeProps, dateParams } = useDateRangeFilter({ resetParams });

  const loadOtherPayments = useCallback(async () => {
    if (!outlet) return;

    setLoading(true);
    try {
      const {
        data: { data, pageInfo },
      } = await outletApi.get("other-payments", {
        params: {
          ...paginationParams,
          ...dateParams,
          ...(keyword && { search: keyword }),
        },
      });
      setOtherPayments(data);
      setRowCount(pageInfo.totalItems);
    } catch (error) {
      showSnackbar(getErrorMessage(error, "Gagal memuat pembayaran"));
    } finally {
      setLoading(false);
    }
  }, [
    outlet,
    outletApi,
    paginationParams,
    dateParams,
    keyword,
    setRowCount,
    showSnackbar,
  ]);

  useEffect(() => {
    loadOtherPayments();
  }, [loadOtherPayments]);

  const toolbarContent = hasPermission(
    PermissionEnum.CREATE_OTHER_PAYMENTS
  ) && (
    <Button fullWidth variant="contained" component={Link} to="create">
      Tambah
    </Button>
  );

  return (
    <PageContainer
      title="Pembayaran"
      breadcrumbs={[]}
      slotProps={{
        header: { slotProps: { toolbar: { children: toolbarContent } } },
      }}
      maxWidth={false}
    >
      <Stack spacing={3}>
        <Grid
          container
          spacing={2}
          sx={{ justifyContent: "flex-end", alignItems: "end" }}
        >
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <UserOutletsSelect
              value={outlet}
              onChange={changeOutlet}
              TextFieldProps={{ label: "Outlet" }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <SearchTextField
              fullWidth
              placeholder="Cari No. Referensi / Catatan"
              {...searchProps}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 12, lg: 6 }}>
            <CustomDateRangePicker {...dateRangeProps} />
          </Grid>
        </Grid>
        <DataGrid
          rows={otherPayments}
          loading={loading}
          getRowHeight={() => "auto"}
          columns={[
            {
              field: "referenceNumber",
              headerName: "Nomor Referensi",
              flex: 1.5,
              sortable: false,
            },
            {
              field: "transactionDate",
              headerName: "Tanggal",
              flex: 1,
              sortable: false,
              valueGetter: (transactionDate) => formatDate(transactionDate),
            },
            {
              field: "cashBankOutletChartOfAccount",
              headerName: "Kas/Bank",
              flex: 2,
              sortable: false,
              valueGetter: (account) =>
                account ? `${account.code} - ${account.name}` : "-",
            },
            {
              field: "payee",
              headerName: "Penerima",
              flex: 1.5,
              sortable: false,
              valueGetter: (payee) => payee || "-",
            },
            {
              field: "amount",
              headerName: "Nilai (Rp)",
              flex: 1.5,
              type: "number",
              sortable: false,
              valueFormatter: (value) => formatNumber(value),
            },
            {
              field: "note",
              headerName: "Catatan",
              flex: 2,
              sortable: false,
              valueGetter: (note) => note || "-",
            },
            {
              field: "actions",
              type: "actions",
              headerName: "Aksi",
              getActions: ({ row }) => {
                const actions = [];
                if (hasPermission(PermissionEnum.READ_OTHER_PAYMENTS)) {
                  actions.push(
                    <IconButton
                      onClick={() => navigate(row.referenceNumber)}
                      size="small"
                    >
                      <VisibilityIcon />
                    </IconButton>
                  );
                }
                return actions;
              },
            },
          ]}
          {...dataGridPaginationProps}
          disableRowSelectionOnClick
        />
      </Stack>
    </PageContainer>
  );
};
